import Markdown from 'react-native-markdown-display';
import {
  ImageStyle,
  StyleProp,
  StyleSheet,
  TextStyle,
  ViewStyle,
} from 'react-native';
import { openWebBrowserURL } from '@/utils/navigation';
import { useTheme } from '@/contexts/theme-context';

interface Props {
  imageStyle?: StyleProp<ImageStyle>;
  markdown: string;
  paragraphStyle?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
}

export const FormattedMarkdown: React.FC<Props> = ({
  imageStyle,
  markdown,
  paragraphStyle,
  textStyle,
}) => {
  const theme = useTheme();

  // Take only family/weight from the `-600` token so the size from `body` applies
  const { fontFamily, fontWeight } = theme.sk.typography.paragraph[
    'base-600'
  ] as TextStyle;

  const semibold: TextStyle = { fontFamily, fontWeight };

  // Wire every common markdown element to the app's typography stack so
  // headings, lists, and emphasized runs all render in the same family as
  // the surrounding UI instead of falling back to the markdown library's
  // system-font defaults.
  return (
    <Markdown
      onLinkPress={(url) => {
        openWebBrowserURL(url);
        // false skips the library's own Linking.openURL fallback
        return false;
      }}
      style={{
        body: {
          ...(theme.sk.typography.paragraph.base as TextStyle),
          // Flatten so registered and array styles spread correctly
          ...StyleSheet.flatten(textStyle),
        },
        em: { fontStyle: 'italic' },
        heading1: theme.sk.typography.heading.xl as TextStyle,
        heading2: theme.sk.typography.heading.lg as TextStyle,
        heading3: theme.sk.typography.heading.md as TextStyle,
        heading4: theme.sk.typography.heading.sm as TextStyle,
        heading5: theme.sk.typography.heading.xs as TextStyle,
        heading6: theme.sk.typography.heading.xs as TextStyle,
        image: StyleSheet.flatten(imageStyle),
        link: semibold,
        paragraph: StyleSheet.flatten(paragraphStyle),
        strong: semibold,
        table: {
          borderColor: theme.sk.color.border.primary,
          borderRadius: theme.sk.radii.radiusSmall,
          borderWidth: 1,
        },
        td: { flex: 1, padding: theme.sk.spacing.spaceXsmall },
        th: { ...semibold, flex: 1, padding: theme.sk.spacing.spaceXsmall },
        thead: { backgroundColor: theme.sk.color.bg.secondary.default },
        tr: {
          borderBottomWidth: 1,
          borderColor: theme.sk.color.border.primary,
          flexDirection: 'row',
        },
      }}
    >
      {markdown}
    </Markdown>
  );
};
